import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Trophy, Sparkles } from 'lucide-react';
import ObjectivesList from './ObjectivesList';

const GoalsModal = ({ isOpen, onClose, project, celebrate = false, currentUser }) => {
  const [showCelebrate, setShowCelebrate] = useState(!!celebrate);
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);
  
  useEffect(() => {
    if (celebrate) {
      setShowCelebrate(true);
      const timeoutId = setTimeout(() => setShowCelebrate(false), 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [celebrate]);
  
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(false);
      setIsMounted(true);
      const t = setTimeout(() => {
        setIsVisible(true);
      }, 24);
      return () => {
        clearTimeout(t);
      };
    } else {
      setIsVisible(false);
      const t = setTimeout(() => setIsMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const objectives = project?.objectives || [];
  const sortedObjectives = [...objectives].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const completedCount = sortedObjectives.filter(o => o.isCompleted === true).length;
  const totalCount = sortedObjectives.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Tour contract (plan_real_guide_gaps.md workstream T): remaining time over
  // the uncompleted pinned stations (walk chain at ~75 m/min + ~5 min dwell
  // per stop) and the next station's name. Only for on-site routes with pins.
  const nextObjective = sortedObjectives.find(o => o.isCompleted !== true);
  const remainingEstimate = (() => {
    if (project?.settings?.venue_mode !== 'on_site_route') return null;
    const pin = (o) => {
      let geo = o?.objectiveExtraData?.geolocation;
      if (Array.isArray(geo)) geo = geo[0];
      const lat = Number(geo?.lat);
      const lng = Number(geo?.lng);
      if (!geo || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
      return { lat, lng };
    };
    const remaining = sortedObjectives
      .filter(o => o.isCompleted !== true)
      .map(pin)
      .filter(Boolean);
    if (remaining.length < 1 || sortedObjectives.length < 2) return null;
    let walkKm = 0;
    for (let i = 1; i < remaining.length; i++) {
      const R = 6371;
      const dLat = ((remaining[i].lat - remaining[i - 1].lat) * Math.PI) / 180;
      const dLng = ((remaining[i].lng - remaining[i - 1].lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((remaining[i - 1].lat * Math.PI) / 180) *
          Math.cos((remaining[i].lat * Math.PI) / 180) *
          Math.sin(dLng / 2) ** 2;
      const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      if (Number.isFinite(d)) walkKm += d;
    }
    // ×1.25 detour factor — matches backend ROUTE_DETOUR_FACTOR
    walkKm *= 1.25;
    const mins = Math.max(5, Math.round(((walkKm * 1000) / 75 + remaining.length * 5) / 5) * 5);
    return mins;
  })();

  if (!isMounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[50001]" role="dialog" aria-modal="true" aria-labelledby="goals-modal-title" ref={modalRef}>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black transition-opacity duration-200 ease-out ${isVisible ? 'bg-opacity-50 opacity-100' : 'bg-opacity-0 opacity-0'}`} onClick={onClose}></div>
      
      {/* Modal panel */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 pointer-events-none">
        <div className={`relative flex flex-col w-full h-full sm:max-h-[85vh] sm:h-auto sm:max-w-xl sm:w-full overflow-hidden bg-white dark:bg-gray-800 shadow-xl transition-all duration-200 ease-out sm:rounded-2xl transform pointer-events-auto ${isVisible ? 'translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-95 opacity-0'}`}>
          
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600/50">
            <div
              className="px-5 py-4 sm:px-6"
              style={{
                paddingTop: 'calc(var(--app-safe-area-top) + 1rem)',
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h1 id="goals-modal-title" className="text-lg font-bold text-gray-900 dark:text-white">
                      Objectives
                    </h1>
                    {totalCount > 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {completedCount} of {totalCount} completed
                        {remainingEstimate != null && completedCount < totalCount && (
                          <> · ~{remainingEstimate} min left</>
                        )}
                        {nextObjective?.objectiveName && completedCount < totalCount && (
                          <> · next: <span className="font-medium text-gray-700 dark:text-gray-300">{nextObjective.objectiveName}</span></>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Progress Bar */}
              {totalCount > 0 && (
                <div className="mt-4">
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ease-out rounded-full ${
                        progressPercent === 100 
                          ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                          : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Celebration Banner */}
          {showCelebrate && (
            <div className="px-5 py-4 sm:px-6 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20 border-b border-green-200/50 dark:border-green-800/30">
              <div className="flex items-center justify-center gap-3">
                <div className="relative">
                  <Trophy className="w-6 h-6 text-yellow-500 animate-bounce" />
                  <Sparkles className="w-4 h-4 absolute -top-1 -right-2 text-yellow-400 animate-pulse" />
                </div>
                <span className="text-base font-semibold text-green-700 dark:text-green-300">
                  Objective completed! 🎉
                </span>
              </div>
            </div>
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto mobile-scroll">
            <div className="px-5 py-5 sm:px-6">
              <ObjectivesList 
                project={project} 
                currentUser={currentUser}
                showHeader={false}
                showAddButton={true}
              />
            </div>
          </div>
          
          {/* Footer */}
          <div className="sticky bottom-0 border-t border-gray-200 dark:border-gray-600/50 bg-white dark:bg-gray-800 px-5 py-4 sm:px-6 flex justify-end items-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
            <button
              onClick={onClose}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 border border-gray-200 dark:border-gray-600 shadow-sm text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default GoalsModal;
